import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { ConfluenceBlogpostAri, ConfluencePageAri } from '@atlassian/ari/confluence';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	resolvePagePdfExportCreateResult,
	type PagePdfExportCreateDecision,
	type PagePdfExportCreateResult,
} from '../pagePdfExportCreateResult.ts';

import type {
	useCreatePagePdfExportTaskMutation$data,
	useCreatePagePdfExportTaskMutation$variables,
} from './__generated__/useCreatePagePdfExportTaskMutation.graphql.ts';

/**
 * The `contentId` mutation input is typed `ID!` but the backend expects a type-specific Confluence
 * content ARI — `blogpost/…` for blogposts, `page/…` for everything else. Live docs are
 * `contentType === 'page'`, so they correctly resolve to a page ARI.
 */
const buildContentAri = (cloudId: string, contentId: string, contentType?: string): string =>
	contentType === 'blogpost'
		? ConfluenceBlogpostAri.create({ siteId: cloudId, blogpostId: contentId }).toString()
		: ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString();

const CREATE_PDF_EXPORT_TASK_MUTATION = graphql`
	mutation useCreatePagePdfExportTaskMutation($contentId: ID!) {
		confluence_createPdfExportTaskForSingleContent(input: { contentId: $contentId }) {
			success
			exportTaskId
			exportVersion
			errors {
				message
				extensions {
					errorType
					statusCode
				}
			}
		}
	}
`;

export type UseCreatePagePdfExportTaskResult = {
	/**
	 * Starts a page PDF export task for the given content and resolves the create result into a
	 * routing decision (legacy / modal / error). Never throws — a failed or thrown create becomes a
	 * `create-failed` error decision. Only call this once the caller has confirmed the page PDF
	 * export experiment is enabled; it always resolves as if the experiment is on.
	 */
	readonly createAndResolve: (
		contentId: string,
		contentType?: string,
	) => Promise<PagePdfExportCreateDecision>;
	/** Whether a create mutation is currently in flight. */
	readonly loading: boolean;
};

/**
 * Shared entry point for the page PDF export create flow, consumed by every user-triggered surface
 * that routes through `confluence_createPdfExportTaskForSingleContent` (the overflow-menu Export to
 * PDF item, the Cmd/Ctrl+P print shortcut, etc.). It owns the mutation, payload normalization, and
 * the create-result → decision mapping so callers only decide how to act on the returned decision
 * (navigate to their legacy URL, open the modal, or show the error state).
 *
 * Defining the hook issues no network request — the mutation only fires when `createAndResolve` runs.
 */
export const useCreatePagePdfExportTask = (): UseCreatePagePdfExportTaskResult => {
	const { cloudId } = useSessionData();
	const [createTask, { loading }] = useMutationInterop<
		useCreatePagePdfExportTaskMutation$data,
		useCreatePagePdfExportTaskMutation$variables
	>(CREATE_PDF_EXPORT_TASK_MUTATION);

	const createAndResolve = useCallback(
		async (contentId: string, contentType?: string): Promise<PagePdfExportCreateDecision> => {
			let createResult: PagePdfExportCreateResult | undefined;
			try {
				const result = await createTask({
					variables: { contentId: buildContentAri(cloudId, contentId, contentType) },
				});
				const payload = result?.data?.confluence_createPdfExportTaskForSingleContent;
				createResult = payload
					? {
							success: payload.success,
							exportVersion: payload.exportVersion,
							exportTaskId: payload.exportTaskId,
							errors:
								payload.errors?.map((error) => ({
									message: error?.message ?? null,
									extensions: error?.extensions
										? {
												errorType: error.extensions.errorType ?? null,
												statusCode: error.extensions.statusCode ?? null,
											}
										: null,
								})) ?? null,
						}
					: undefined;
			} catch (error) {
				createResult = undefined;
			}

			return resolvePagePdfExportCreateResult({ createResult, isExperimentEnabled: true });
		},
		[createTask, cloudId],
	);

	return { createAndResolve, loading };
};
